import { useEffect, useMemo, useState } from "react";
import { SUPPORTED_COUNTRIES, setCountry, getCountry, TEK_ULKE } from "../country";

/**
 * ÜLKE SEÇİM EKRANI — TUR-9 (29 Ağu 2026).
 *
 * NEDEN VAR
 * ---------
 * Tek uygulama, iki yüz: Türkiye'de Sokak Kedileri (yalnızca kedi),
 * yurt dışında StrayPaw (kedi + köpek). Hangi yüzün açılacağını `getCountry()`
 * belirliyor ve o da `sk_country`'ye bakıyor.
 *
 * TUR-8'e kadar bu değer cihazın dilinden TAHMİN ediliyordu. O tahmin
 * Türkiye'deki Arapça telefonları "eg" sanıp köpek arayüzünü açıyordu; katman
 * kaldırıldı. Kaldırılınca da uygulama HERKESİ Türkiye sayar oldu — yani yurt
 * dışı hiç açılamaz. Bu ekran o boşluğu dolduruyor: tahmin yerine SEÇİM.
 *
 * MELEZ DAVRANIŞ (uygulama planı §3-1)
 * ------------------------------------
 * Cihazın bölge kodu net ise (`id-ID` → id) o ülke ÖNERİ olarak seçili gelir;
 * kullanıcı tek dokunuşla onaylar. Bölge kodu yoksa veya desteklenmiyorsa
 * hiçbir şey seçili gelmez, kullanıcı listeden seçer.
 *
 * Tahmin artık SESSİZ bir yedek değil, EKRANDAKİ bir öneri. Fark kritik:
 * sessiz yedek yanlış olduğunda kimse fark etmez (TUR-8'in konusu), ekrandaki
 * öneri yanlış olduğunda kullanıcı düzeltir.
 *
 * MEVCUT KULLANICIYA GÖSTERİLMEZ
 * ------------------------------
 * Tek binary olduğu için Türk kullanıcı da bu ekranı görebilirdi. Görmemeli:
 * bugüne kadar uygulamayı kullanmış biri için ülke zaten belli. `zatenBiliniyor`
 * bunu üç işaretten anlıyor — seçilmiş ülke, jeton, görülmüş açılış turu.
 * Hiçbiri yoksa kişi gerçekten yeni demektir, ancak o zaman soruluyor.
 *
 * Metinler burada, i18n.js'te değil — `IlkAcilisTuru` ile aynı kalıp. Dil
 * bloğu olmayan bir dil İngilizce'ye düşer, Türkçe'ye ASLA.
 */
const ANAHTAR = "sk_country";

// Ülke adları, kullanıcının dilinde.
const ULKE_ADI = {
  tr: { tr: "Türkiye", id: "Endonezya", br: "Brezilya", in: "Hindistan", eg: "Mısır", pl: "Polonya", es: "İspanya" },
  en: { tr: "Türkiye", id: "Indonesia", br: "Brazil", in: "India", eg: "Egypt", pl: "Poland", es: "Spain" },
  id: { tr: "Turki", id: "Indonesia", br: "Brasil", in: "India", eg: "Mesir", pl: "Polandia", es: "Spanyol" },
};

const BAYRAK = { tr: "🇹🇷", id: "🇮🇩", br: "🇧🇷", in: "🇮🇳", eg: "🇪🇬", pl: "🇵🇱", es: "🇪🇸" };

/** Ülke adı, verilen dilde; dil bilinmiyorsa İngilizce (Türkçe'ye ASLA). */
export function ulkeAdi(kod, dil) {
  const sozluk = ULKE_ADI[dil] || ULKE_ADI.en;
  return sozluk[kod] || String(kod || "").toUpperCase();
}

export function ulkeBayragi(kod) {
  return BAYRAK[kod] || "🏳️";
}

/**
 * ULKE-2 (30 Ağu 2026) — ekranı sonradan yeniden açan olay.
 *
 * Ekran kullanıcıya "sonradan değiştirebilirsin" diyordu ve değiştirmenin
 * HİÇBİR yolu yoktu: `setCountry()` yalnızca bu dosyadan çağrılıyordu, bu
 * ekran da `sk_country` yazıldıktan sonra bir daha hiç açılmıyordu. Yanlış
 * ülkeye dokunan bir kullanıcı o topluluğa kilitleniyordu — uygulamayı silip
 * kurmak dışında çıkışı yoktu, üstelik ekranda çıkabileceği yazıyordu.
 *
 * Olay kullanılmasının sebebi: ekran uygulamanın kökünde (`App.js`) duruyor,
 * Profil ise ağacın çok altında. Aradaki her katmana bir prop geçirmek yerine
 * tek bir olay yeterli.
 */
export const ULKE_DEGISTIR_OLAYI = "sk:ulke-degistir";

/** Profil gibi yerlerden çağrılır: ülke ekranını yeniden açar. */
export function ulkeEkraniniAc(lang) {
  try {
    window.dispatchEvent(new CustomEvent(ULKE_DEGISTIR_OLAYI, { detail: { lang } }));
  } catch {
    /* CustomEvent yoksa (çok eski webview) sessizce yok say */
  }
}

const METIN = {
  tr: {
    baslik: "Hangi ülkedesin?",
    alt: "Topluluğunu seç. Mahallendeki kayıtlar, gönüllüler ve mesajlar bu ülkeden gelir.",
    uyari: "Ülke seçimi bir görünüm ayarı değildir: hesabın, kayıtların ve mesajların seçtiğin ülkede kalır. Sonradan değiştirebilirsin, ama farklı bir topluluğa geçmiş olursun.",
    devam: "Devam et",
    sec: "Bir ülke seç",
    vazgec: "Vazgeç",
    baslikDegistir: "Ülkeni değiştir",
    henuzYok: "StrayPaw henüz senin bulunduğun ülkede açık değil.",
    henuzYokAlt: "Aşağıdaki ülkelerden birini seçerek gezinebilirsin — ama kayıtların ve mesajların o ülkenin topluluğuna düşer.",
  },
  en: {
    baslik: "Which country are you in?",
    alt: "Choose your community. Records, volunteers and messages come from this country.",
    uyari: "This is not a display setting: your account, records and messages stay in the country you pick. You can change it later, but you will be moving to a different community.",
    devam: "Continue",
    sec: "Select a country",
    vazgec: "Cancel",
    baslikDegistir: "Change your country",
    henuzYok: "StrayPaw isn't open in your country yet.",
    henuzYokAlt: "You can still look around by picking one of the countries below — but your records and messages will belong to that country's community.",
  },
  id: {
    baslik: "Kamu ada di negara mana?",
    alt: "Pilih komunitasmu. Data, relawan, dan pesan berasal dari negara ini.",
    uyari: "Ini bukan sekadar pengaturan tampilan: akun, data, dan pesanmu tetap berada di negara yang kamu pilih. Kamu bisa mengubahnya nanti, tetapi itu berarti pindah ke komunitas lain.",
    devam: "Lanjut",
    sec: "Pilih negara",
    vazgec: "Batal",
    baslikDegistir: "Ubah negaramu",
    henuzYok: "StrayPaw belum tersedia di negaramu.",
    henuzYokAlt: "Kamu tetap bisa melihat-lihat dengan memilih salah satu negara di bawah — tetapi data dan pesanmu akan masuk ke komunitas negara tersebut.",
  },
};

/**
 * Tek ülkeli bir alan adında mıyız?
 *
 * `sokakkedileri.tr` tanım gereği tek ülkeli bir yüz: oraya gelen ziyaretçiye
 * "hangi ülkedesin?" diye sormak anlamsız — alan adı zaten cevabı söylüyor.
 * `straypaw.ai` ise çok ülkeli; oradaki ziyaretçi Brezilyalı da olabilir,
 * Meksikalı da, sorulmalı. Native uygulamada alan adı yok (Capacitor
 * `localhost` üzerinden çalışır) ve tek binary iki yüzü de taşıdığı için
 * sorulmalı.
 *
 * Yön bilinçli: BİLİNMEYEN durumda SORUYORUZ. Yanlışlıkla sormak küçük bir
 * rahatsızlık; yanlışlıkla sormamak kullanıcıyı sessizce Türkiye'ye kilitler
 * ve kimse fark etmez. TUR-9'un kendi dersi (yokluğun sızıntısı görünmez).
 */
const TEK_ULKELI_ALANLAR = ["sokakkedileri.tr", "sokakkedileri.pages.dev"];

export function tekUlkeliAlanAdi() {
  // TEK-ULKE: derleme zamanı kilidi varsa alan adına hiç bakma. Android'in
  // Sokak Kedileri paketi bununla derleniyor; ekran orada hiç açılmamalı.
  if (TEK_ULKE) return true;
  try {
    const h = String(window.location.hostname || "").toLowerCase();
    return TEK_ULKELI_ALANLAR.some((a) => h === a || h.endsWith("." + a) || h.endsWith(a));
  } catch {
    return false;   // bilinmiyorsa sor
  }
}

/** Bu cihazda ülke zaten biliniyor mu — mevcut kullanıcıya ekran gösterilmez. */
export function zatenBiliniyor() {
  try {
    if (localStorage.getItem(ANAHTAR)) return true;     // seçilmiş
    if (localStorage.getItem("token")) return true;     // giriş yapmış
    if (localStorage.getItem("sk_tur_v1")) return true; // açılış turunu görmüş
    if (tekUlkeliAlanAdi()) return true;                // sokakkedileri.tr — sormaya gerek yok
    return false;
    // NOT: dil anahtarları ("lang" / "sk_lang") işaret olarak KULLANILMAZ —
    // i18n onları her açılışta yazıyor, yani yepyeni bir kullanıcıda da dolu
    // olurlar. İşaret sayılsalardı ekran hiç açılmaz, yurt dışı hiç açılamazdı.
  } catch {
    return true; // localStorage yoksa soramayız; sessizce varsayılana düş
  }
}

/**
 * Cihazın BÖLGE kodundan öneri. TUR-8'den önce `country.js` içindeydi ve
 * sessizce uygulanıyordu; artık yalnızca burada, yalnızca öneri olarak.
 *
 * Dilden tahmin BİLEREK yapılmıyor — "ar" → Mısır çıkarımı tam olarak TUR-8'in
 * kapattığı hataydı. Yalnızca açık bölge kodu (`id-ID`) sayılır.
 */
export function bolgedenOneri() {
  try {
    const diller = [];
    if (typeof navigator !== "undefined") {
      if (navigator.languages) diller.push(...navigator.languages);
      if (navigator.language) diller.push(navigator.language);
    }
    for (const d of diller) {
      const parca = String(d).split(/[-_]/)[1];
      if (!parca) continue;
      const kod = parca.trim().toLowerCase().slice(0, 2);
      if (SUPPORTED_COUNTRIES.includes(kod)) return kod;
    }
  } catch {
    /* navigator yoksa öneri yok */
  }
  return null;
}

/**
 * ULKE-3 (3 Eyl 2026): cihazın bildirdiği HAM bölge kodu — desteklensin ya da
 * desteklenmesin.
 *
 * `bolgedenOneri()` desteklenmeyen kodu null'a çeviriyor, yani "Japonya'dayım"
 * ile "nerede olduğum belli değil" ayırt edilemiyordu. İkisi aynı ekranı
 * görüyordu: yedi ülkelik bir liste ve hiçbiri doğru değil. Kullanıcının tek
 * çıkışı olmadığı bir yeri seçmekti — ve o seçim görünüm ayarı değil; fotoğrafın
 * hangi bucket'a gideceğini, haritanın nereye açılacağını ve hangi topluluğa
 * gireceğini belirliyor. Yani uygulama kişiyi sessizce yanlış veri üretmeye
 * zorluyordu.
 *
 * Bu fonksiyon farkı görünür kılıyor; ekran da bunu söyleyebiliyor.
 * Dilden ÇIKARIM YOK, yalnızca açık bölge kodu (`ja-JP` → "jp") — dilden ülke
 * tahmini TUR-8'in kapattığı hataydı ("ar" → Mısır).
 */
export function hamBolgeKodu() {
  try {
    const diller = [];
    if (typeof navigator !== "undefined") {
      if (navigator.languages) diller.push(...navigator.languages);
      if (navigator.language) diller.push(navigator.language);
    }
    for (const d of diller) {
      const parca = String(d).split(/[-_]/)[1];
      if (!parca) continue;
      const kod = parca.trim().toLowerCase().slice(0, 2);
      if (/^[a-z]{2}$/.test(kod)) return kod;
    }
  } catch {
    /* navigator yoksa bilgi yok */
  }
  return null;
}

/**
 * Bu ekranın dili CİHAZDAN gelir, uygulamanın dilinden DEĞİL.
 *
 * `i18n.defaultLang()` dili `getCountry()`'den türetiyor. TUR-8'den sonra
 * getCountry() herkese "tr" dönüyor — yani ülke seçilene kadar uygulamanın
 * dili Türkçe. Bu ekran uygulamanın dilini kullansaydı, Endonezyalı
 * kullanıcıya "Hangi ülkedesin?" sorusu TÜRKÇE sorulurdu: cevabı ülkeye bağlı
 * olan bir soruyu, ülkeye bağlı bir dille sormak.
 *
 * Tavuk-yumurta. Kırılma noktası: bu tek ekran için dili cihazdan al.
 */
export function cihazDili() {
  try {
    const d = (navigator.languages && navigator.languages[0]) || navigator.language || "";
    const kod = String(d).split(/[-_]/)[0].toLowerCase();
    return METIN[kod] ? kod : "en";   // bloğu olmayan dil İngilizce'ye düşer
  } catch {
    return "en";
  }
}

export default function UlkeSecimi({ lang, onSecildi }) {
  const [acik, setAcik] = useState(false);
  const [secili, setSecili] = useState(null);
  // ULKE-2: ekran iki farklı sebeple açılabiliyor ve ikisi FARKLI davranıyor.
  //   • ilk açılış — ülke bilinmiyor, vazgeçilemez (bir seçim şart)
  //   • yeniden açış — Profil'den geliniyor, vazgeçilebilir olmalı
  // Vazgeçme ilk açılışta sunulsaydı kullanıcı ülkesiz kalır, uygulama da
  // "tr"ye düşerdi — yani sessizce Türkiye'ye.
  const [yeniden, setYeniden] = useState(false);
  const [olayDili, setOlayDili] = useState(null);

  // `lang` yalnızca testler ve bilinçli çağrılar için; verilmezse cihaz dili.
  // Yeniden açılışta uygulamanın GERÇEK dili olayla geliyor — o an cihaz
  // dilini kullanmak Endonezce kullanan birine soruyu İngilizce sorardı.
  const dil = METIN[olayDili] ? olayDili : METIN[lang] ? lang : cihazDili();

  useEffect(() => {
    if (!zatenBiliniyor()) {
      setSecili(bolgedenOneri());
      setAcik(true);
    }
  }, []);

  useEffect(() => {
    const dinle = (e) => {
      let mevcut = null;
      try { mevcut = getCountry(); } catch { /* okunamadı, seçimsiz aç */ }
      setSecili(mevcut);                       // mevcut ülke seçili gelsin
      setOlayDili(e?.detail?.lang || null);
      setYeniden(true);
      setAcik(true);
    };
    window.addEventListener(ULKE_DEGISTIR_OLAYI, dinle);
    return () => window.removeEventListener(ULKE_DEGISTIR_OLAYI, dinle);
  }, []);

  const t = METIN[dil] || METIN.en;
  const adlar = ULKE_ADI[dil] || ULKE_ADI.en;

  // Önerilen ülke en üstte; kalanlar kendi dilindeki adına göre sıralı.
  // Sıra yalnızca ekran açılırken kuruluyor ki kullanıcı seçtikçe zıplamasın.
  const ilkOneri = useMemo(() => (acik ? secili : null), [acik]);  // eslint-disable-line react-hooks/exhaustive-deps

  // ULKE-3: cihaz açık bir bölge kodu bildiriyor ve o ülke listede YOK mu?
  // Üç hâl var ve üçü farklı: (a) kod yok — bilgi yok, susarız; (b) kod var ve
  // destekleniyor — zaten önerilmiş; (c) kod var ve desteklenmiyor — söylenecek
  // şey tam da bu. Ekran açılırken bir kez hesaplanır; kullanıcı seçtikçe
  // değişmemeli, yoksa uyarı gözünün önünde kaybolur.
  const desteklenmeyenBolge = useMemo(() => {
    if (!acik) return false;
    const kod = hamBolgeKodu();
    return !!kod && !SUPPORTED_COUNTRIES.includes(kod);
  }, [acik]);
  const sirali = useMemo(() => {
    const kalan = SUPPORTED_COUNTRIES.filter((c) => c !== ilkOneri);
    kalan.sort((a, b) => String(adlar[a] || a).localeCompare(String(adlar[b] || b)));
    return ilkOneri ? [ilkOneri, ...kalan] : kalan;
  }, [ilkOneri, adlar]);

  if (!acik) return null;

  const kapat = () => {
    setAcik(false);
    setYeniden(false);
    setOlayDili(null);
  };

  const onayla = () => {
    if (!secili) return;
    // ULKE-2: Profil'den açılıp aynı ülke onaylanırsa yeniden yükleme yapma —
    // hiçbir şey değişmedi, sayfayı boşuna sıfırlamak kullanıcıyı bulunduğu
    // yerden atar.
    let oncekiUlke = null;
    try { oncekiUlke = getCountry(); } catch { /* okunamadı */ }
    if (yeniden && secili === oncekiUlke) {
      kapat();
      return;
    }
    if (!setCountry(secili)) return;
    setAcik(false);
    if (typeof onSecildi === "function") onSecildi(secili);
    // Ülke her şeyi süzüyor: backend adresi, tür kapıları, şehir listesi,
    // harita merkezi. Tek tek bileşen yenilemek yerine tam yeniden yükleme —
    // tutarlı ve ucuz, çünkü bu yalnızca ilk açılışta bir kez oluyor.
    try { window.location.reload(); } catch { /* test ortamı */ }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] bg-white overflow-y-auto"
      data-testid="ulke-secimi"
      role="dialog"
      aria-modal="true"
    >
      <div className="mx-auto w-full max-w-sm px-6 pt-12 pb-8">
        <div className="text-5xl text-center" aria-hidden>🐾</div>
        <h1 className="mt-4 text-center font-display font-bold text-xl text-stone-900">
          {yeniden ? t.baslikDegistir : t.baslik}
        </h1>
        <p className="mt-2 text-center text-sm text-stone-600 leading-relaxed">{t.alt}</p>

        {/* ULKE-3: cihaz açıkça DESTEKLENMEYEN bir ülke bildiriyorsa bunu söyle.
            Sessiz kalmak, kişiyi olmadığı bir yeri seçmeye iter ve o seçim
            veri ikametgahını belirler. Liste yine de gösteriliyor: kimseyi
            kapıda bırakmıyoruz, sadece ne yaptığını bilerek seçsin.
            Yalnızca ilk kurulumda (`yeniden` değilken) çıkar — Profil'den
            ülkesini değiştiren biri bunu zaten biliyor. */}
        {!yeniden && desteklenmeyenBolge && (
          <div
            className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3"
            data-testid="ulke-desteklenmiyor"
          >
            <p className="text-sm font-semibold text-amber-900">{t.henuzYok}</p>
            <p className="mt-1 text-[13px] text-amber-800 leading-relaxed">{t.henuzYokAlt}</p>
          </div>
        )}

        <div className="mt-6 space-y-2">
          {sirali.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setSecili(c)}
              data-testid={`ulke-${c}`}
              aria-pressed={secili === c}
              className={`w-full flex items-center gap-3 rounded-2xl border-2 px-4 py-3 text-left transition ${
                secili === c
                  ? "border-orange-500 bg-orange-50"
                  : "border-stone-200 bg-white hover:border-stone-300"
              }`}
            >
              <span className="text-2xl" aria-hidden>{BAYRAK[c] || "🏳️"}</span>
              <span className="font-semibold text-stone-800">{adlar[c] || c.toUpperCase()}</span>
            </button>
          ))}
        </div>

        <p className="mt-5 text-[12px] text-stone-500 leading-relaxed">{t.uyari}</p>

        <button
          type="button"
          onClick={onayla}
          disabled={!secili}
          data-testid="ulke-onayla"
          className="mt-5 w-full h-12 rounded-2xl bg-orange-500 text-white font-bold disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.99] transition"
        >
          {secili ? t.devam : t.sec}
        </button>

        {/* Vazgeç YALNIZCA yeniden açılışta. İlk açılışta sunulsaydı kullanıcı
            ülkesiz kalır ve uygulama sessizce "tr"ye — yani Türkiye'ye —
            düşerdi. */}
        {yeniden && (
          <button
            type="button"
            onClick={kapat}
            data-testid="ulke-vazgec"
            className="mt-3 w-full h-11 rounded-2xl text-stone-500 font-semibold hover:bg-stone-50 transition"
          >
            {t.vazgec}
          </button>
        )}
      </div>
    </div>
  );
}
